import { useCallback } from 'react';
import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Icon } from '#/components/Icon/Icon';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaHit } from '#/modules/search/algolia/types/SearchAlgoliaHit';
import { useSearchSavedListingsHit } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypePluralHumanNameMap';

import { StyledSearchHitFloatingSaveButtonEl } from './StyledSearchHitFloatingSaveButtonEl';

type Props = {
  hit: SearchAlgoliaHit;
  searchSavedListingsStore: SearchSavedListingsStore;
};

/**
 * The save button cannot be within the `container-type` div because that causes
 * the tooltip to be below the next search result tile
 */
const SearchHitFloatingSaveButtonHolder = styled.div`
  display: flex;
  position: absolute;
  top: 15px;
  right: 15px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

export function SearchHitFloatingSaveButton({
  hit,
  searchSavedListingsStore,
}: Props) {
  const { i18n } = useI18n();

  const {
    listingSaved,
    saveListing,
    unsaveListing,
    saveOrg,
    dismissSaveOrgTooltip,
    shouldShowSaveOrgTooltip,
  } = useSearchSavedListingsHit({
    hit,
    searchSavedListingsStore,
  });

  const onConfirm = async () => {
    trackClicked('Save Org Tooltip', { cta_type: 'yes' });
    await saveOrg();
  };

  const onDecline = () => {
    dismissSaveOrgTooltip();
    trackClicked('Save Org Tooltip', { cta_type: 'no' });
  };

  const onRender = useCallback(
    () => trackEvent('Displayed: Save Org Tooltip'),
    [],
  );

  const listingName = typeof hit.name === 'string' ? hit.name.trim() : '';
  let saveButtonLabel: string;
  if (listingSaved) {
    saveButtonLabel = listingName
      ? i18n('Remove saved %(listingName)s', { listingName })
      : i18n('Remove saved listing');
  } else {
    saveButtonLabel = listingName
      ? i18n('Save %(listingName)s', { listingName })
      : i18n('Save listing');
  }

  return (
    <SearchHitFloatingSaveButtonHolder>
      <Tooltip
        tooltipWidth={{ xs: '250px', lg: '300px' }}
        content={
          <div data-qa-id="save-listing-tooltip">
            <Box mb={3}>
              {i18n(
                'Would you like to get alerts when this organization posts new %(listingTypeName)s?',
                {
                  listingTypeName:
                    LISTING_TYPE_PLURAL_HUMAN_NAME_MAP(i18n)[hit.type],
                },
              )}
            </Box>

            <ButtonContainer>
              <Button
                type="button"
                variant="secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  onDecline();
                }}
                qaId="decline-button"
              >
                {i18n('No, thank you')}
              </Button>

              <Button
                type="button"
                variant="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  void onConfirm();
                }}
                qaId="confirm-button"
              >
                {i18n('Yes!')}
              </Button>
            </ButtonContainer>
          </div>
        }
        placement="bottom-left"
        closeable
        size="large"
        variant="brand-blue"
        renderCondition={shouldShowSaveOrgTooltip}
        onRender={onRender}
      >
        <StyledSearchHitFloatingSaveButtonEl
          $saved={listingSaved}
          onClick={() => {
            if (listingSaved) {
              void unsaveListing();
            } else {
              void saveListing();
            }
          }}
          aria-label={saveButtonLabel}
          title={saveButtonLabel}
          data-qa-id={
            listingSaved ? 'unsave-listing-search' : 'save-listing-search'
          }
        >
          <Icon
            name={listingSaved ? 'bookmark-filled' : 'bookmark'}
            size={16}
            aria-hidden="true"
          />
        </StyledSearchHitFloatingSaveButtonEl>
      </Tooltip>
    </SearchHitFloatingSaveButtonHolder>
  );
}
